const GENERIC_CITY = /^(unknown|unspecified|n\/a|null|—|-)?$/i;

function asCount(value) {
  const n = Number(value);
  return Number.isFinite(n) && n > 0 ? n : 0;
}

export function isGenericMarketCity(name) {
  return GENERIC_CITY.test(String(name || "").trim());
}

export function groupPartnerMarketCities(cities = []) {
  const byCity = new Map();
  for (const row of cities) {
    const rawName = String(row?.city || "").trim();
    const name = isGenericMarketCity(rawName) ? "" : rawName;
    if (!name) {
      continue;
    }
    const entry = byCity.get(name) || {
      city: name,
      sale: null,
      rent: null,
      properties: 0,
    };
    const type = String(row?.listingType || "").toUpperCase();
    if (type === "SALE" || type === "FOR_SALE") {
      entry.sale = row;
    } else if (type === "RENT" || type === "FOR_RENT") {
      entry.rent = row;
    }
    entry.properties += asCount(row?.properties);
    byCity.set(name, entry);
  }
  return [...byCity.values()].sort((a, b) => b.properties - a.properties);
}

export function resolveInventoryTone(created, gone) {
  const net = created - gone;
  if (net > 0) return "expanding";
  if (net < 0) return "tightening";
  return "steady";
}

export function resolvePriceTone(increases, decreases) {
  if (increases <= 0 && decreases <= 0) return "none";
  if (decreases > increases) return "cutting";
  if (increases > decreases) return "raising";
  return "balanced";
}

export function buildPartnerMarketSnapshotStory(trends) {
  const summary = trends?.summary || {};
  const available = asCount(summary.availableUniqueProperties);
  const created = asCount(summary.created);
  const gone = asCount(summary.becameUnavailable);
  const increases = asCount(summary.priceIncreases);
  const decreases = asCount(summary.priceDecreases);
  const priceMoves = increases + decreases;
  const netFlow = created - gone;
  const cutShare = priceMoves > 0 ? decreases / priceMoves : null;
  const cities = groupPartnerMarketCities(trends?.cities);
  const churnLooksSettling =
    available > 0 && created > 0 && created === gone && created / available >= 0.45;
  const recentPriceChanges = (trends?.recentPriceChanges || []).filter((row) => {
    const previous = Number(row?.previousPrice);
    const next = Number(row?.newPrice);
    return Number.isFinite(previous) && Number.isFinite(next) && previous !== next;
  });
  const weekly = Array.isArray(trends?.weekly) ? trends.weekly : [];
  const hasSignal = available > 0 || created > 0 || gone > 0 || priceMoves > 0 || cities.length > 0;

  return {
    hasSignal,
    windowDays: Number(trends?.windowDays) || 30,
    available,
    created,
    gone,
    increases,
    decreases,
    priceMoves,
    netFlow,
    cutShare,
    inventoryTone: churnLooksSettling ? "unclear" : resolveInventoryTone(created, gone),
    churnLooksSettling,
    priceTone: resolvePriceTone(increases, decreases),
    cities,
    recentPriceChanges,
    weekly,
    deltas: summary.deltas || {},
  };
}

export function formatSignedCount(value) {
  const n = Number(value) || 0;
  if (n > 0) return `+${n.toLocaleString()}`;
  return n.toLocaleString();
}

export function priceChangeDirection(row) {
  const previous = Number(row?.previousPrice);
  const next = Number(row?.newPrice);
  if (Number.isFinite(previous) && Number.isFinite(next)) {
    if (next < previous) return "down";
    if (next > previous) return "up";
  }
  const type = String(row?.changeType || "").toLowerCase();
  if (type.includes("decrease") || type.includes("down") || type.includes("cut")) return "down";
  if (type.includes("increase") || type.includes("up")) return "up";
  return "flat";
}
